/**
 * The one box a video shows when it can't play: still processing, not available (404/403, no
 * Retry), or failed (with Retry). Same size as the player, so nothing jumps. Shared (Speedies
 * build it first; chat adopts it in ticket 14zfy41wxvm). Imports nothing Speedy-specific.
 */
import { AlertTriangle, Clock, RotateCcw, VideoOff } from 'lucide-react';

export interface MediaErrorStateProps {
  kind: 'processing' | 'unavailable' | 'failed';
  /** width ÷ height of the box. */
  aspect: number;
  maxWidth?: number;
  posterUrl?: string | null;
  onRetry?: () => void;
  /** Override the default sentence. */
  message?: string;
  className?: string;
}

const COPY: Record<MediaErrorStateProps['kind'], string> = {
  processing: 'This video is still processing',
  unavailable: "This video isn't available",
  failed: "Couldn't play this video",
};

export function MediaErrorState({ kind, aspect, maxWidth, posterUrl, onRetry, message, className = '' }: MediaErrorStateProps) {
  const Icon = kind === 'processing' ? Clock : kind === 'unavailable' ? VideoOff : AlertTriangle;
  return (
    <div
      className={`relative flex w-full items-center justify-center overflow-hidden bg-black text-white ${className}`}
      style={{ aspectRatio: aspect > 0 ? String(aspect) : undefined, maxWidth }}
      role="status"
    >
      {posterUrl && <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />}
      <div className="relative flex flex-col items-center gap-3 px-6 text-center">
        <Icon className="h-7 w-7 opacity-80" aria-hidden />
        <p className="text-sm font-medium">{message ?? COPY[kind]}</p>
        {kind === 'failed' && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white/15 px-4 text-sm font-semibold hover:bg-white/25"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
